import { useDndMonitor, useDraggable } from "@dnd-kit/core";
import { Coordinates } from "@dnd-kit/utilities";
import { useEffect, useLayoutEffect, useState } from "react";
import { FaCheck } from "react-icons/fa";
import type { StickyPositionMode, UpdateStickyInput } from "./useStickies";
type MeasuredRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

interface StickyProps {
  id: number;
  message: string;
  initialX: number;
  initialY: number;
  anchorId: string;
  positionMode: StickyPositionMode;
  relativeX: number | null;
  relativeY: number | null;
  isMutating: boolean;
  onUpdate: (input: UpdateStickyInput) => Promise<unknown>;
}

const getStickyPositionMode = (anchorId: string): StickyPositionMode => {
  if (
    anchorId === "weekChooser" ||
    anchorId === "agendaMainSettingsButton" ||
    anchorId.startsWith("dayHeader-")
  ) {
    return "relative";
  }
  return "pixel";
};

const toRoundedRelativeCoordinate = (value: number) => {
  return Math.round(value * 10000) / 10000;
};

const getRelativeCoordinatesFromRects = (
  droppableRect: MeasuredRect,
  draggableRect: MeasuredRect,
) => {
  return {
    x:
      droppableRect.width === 0
        ? 0
        : toRoundedRelativeCoordinate(
            (draggableRect.left - droppableRect.left) / droppableRect.width,
          ),
    y:
      droppableRect.height === 0
        ? 0
        : toRoundedRelativeCoordinate(
            (draggableRect.top - droppableRect.top) / droppableRect.height,
          ),
  };
};

const getPixelCoordinatesFromRelativePosition = (
  anchorId: string,
  relativeX: number,
  relativeY: number,
) => {
  const anchorElement = document.getElementById(anchorId);
  if (!anchorElement) {
    return null;
  }
  const anchorRect = anchorElement.getBoundingClientRect();
  return {
    x: Math.round(anchorRect.width * relativeX),
    y: Math.round(anchorRect.height * relativeY),
  };
};

const Sticky = (props: StickyProps) => {
  const [{ x, y }, setCoordinates] = useState<Coordinates>({
    x: props.initialX,
    y: props.initialY,
  });
  const [newAnchor, setAnchor] = useState(props.anchorId);
  const [positionMode, setPositionMode] = useState<StickyPositionMode>(
    props.positionMode,
  );
  const [relativeCoordinates, setRelativeCoordinates] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: props.relativeX,
    y: props.relativeY,
  });
  const [text, setText] = useState(props.message);
  useEffect(() => {
    setCoordinates({ x: props.initialX, y: props.initialY });
    setAnchor(props.anchorId);
    setPositionMode(props.positionMode);
    setRelativeCoordinates({
      x: props.relativeX,
      y: props.relativeY,
    });
  }, [
    props.anchorId,
    props.initialX,
    props.initialY,
    props.positionMode,
    props.relativeX,
    props.relativeY,
  ]);
  useEffect(() => {
    setText(props.message);
  }, [props.message]);
  const [isEditing, setIsEditing] = useState(false);

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `sticky-${props.id}`,
    disabled: isEditing || props.isMutating,
  });

  const syncRelativeCoordinates = () => {
    if (
      positionMode !== "relative" ||
      relativeCoordinates.x === null ||
      relativeCoordinates.y === null
    ) {
      return;
    }
    const nextCoordinates = getPixelCoordinatesFromRelativePosition(
      newAnchor,
      relativeCoordinates.x,
      relativeCoordinates.y,
    );
    if (!nextCoordinates) {
      return;
    }
    setCoordinates((previousCoordinates) => {
      if (
        previousCoordinates.x === nextCoordinates.x &&
        previousCoordinates.y === nextCoordinates.y
      ) {
        return previousCoordinates;
      }
      return nextCoordinates;
    });
  };

  useLayoutEffect(() => {
    syncRelativeCoordinates();
  });

  useLayoutEffect(() => {
    if (positionMode !== "relative") {
      return;
    }
    const handleResize = () => {
      syncRelativeCoordinates();
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [newAnchor, positionMode, relativeCoordinates.x, relativeCoordinates.y]);

  const handleCommit = ({
    anchorId,
    offsetX,
    offsetY,
    nextPositionMode,
    nextRelativeX,
    nextRelativeY,
  }: {
    anchorId?: string;
    offsetX?: number;
    offsetY?: number;
    nextPositionMode?: StickyPositionMode;
    nextRelativeX?: number | null;
    nextRelativeY?: number | null;
  }) => {
    const committedAnchorId = anchorId ?? newAnchor;
    const committedPositionMode = nextPositionMode ?? positionMode;
    const committedRelativeX =
      nextRelativeX === undefined ? relativeCoordinates.x : nextRelativeX;
    const committedRelativeY =
      nextRelativeY === undefined ? relativeCoordinates.y : nextRelativeY;

    props
      .onUpdate({
        id: props.id,
        content: text,
        offsetX: offsetX ?? x,
        offsetY: offsetY ?? y,
        anchorId: committedAnchorId,
        positionMode: committedPositionMode,
        relativeX: committedRelativeX,
        relativeY: committedRelativeY,
      })
      .catch(() => {});
    setIsEditing(false);
  };

  useDndMonitor({
    onDragEnd: (event) => {
      if (event.active.id !== `sticky-${props.id}`) return;
      if (event.over && event.active.rect && event.over.rect) {
        if (event.over.id === "stickiesOrigin") return;
        const droppableRect = event.over.rect;
        const draggableRect =
          event.active.rect.current.translated ||
          event.active.rect.current.initial;

        const offsetX = Math.round(
          draggableRect ? draggableRect.left - droppableRect.left : 0,
        );
        const offsetY = Math.round(
          draggableRect ? draggableRect.top - droppableRect.top : 0,
        );
        const id = event.over.id;
        if (typeof id !== "string") return;
        const nextPositionMode = getStickyPositionMode(id);
        const nextRelativeCoordinates =
          draggableRect && nextPositionMode === "relative"
            ? getRelativeCoordinatesFromRects(droppableRect, draggableRect)
            : { x: null, y: null };

        setCoordinates({ x: offsetX, y: offsetY });
        setAnchor(id);
        setPositionMode(nextPositionMode);
        setRelativeCoordinates(nextRelativeCoordinates);
        handleCommit({
          anchorId: id,
          offsetX,
          offsetY,
          nextPositionMode,
          nextRelativeX: nextRelativeCoordinates.x,
          nextRelativeY: nextRelativeCoordinates.y,
        });
      }
    },
  });
  const transformStyle = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : undefined;
  const style = {
    ...transformStyle,
    top: `calc(anchor(--${newAnchor} top) + ${y}px)`,
    left: `calc(anchor(--${newAnchor} left) + ${x}px)`,
    transition: "height 0.2s ease",
    positionAnchor: `--${newAnchor}`,
  };
  const handleTextAreaKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>,
  ) => {
    //commit on escape
    if (e.key === "Escape") {
      handleCommit({});
    }
  };
  return (
    <div
      className="bg-yellow-200 p-4 rounded-sm shadow-lg absolute w-48 h-14 hover:h-48 opacity-80 hover:opacity-100 pt-2 overflow-hidden hover:overflow-visible text-sm z-10"
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={style}
      onDoubleClick={() => setIsEditing(true)}
    >
      {isEditing ? (
        <div className="flex flex-col h-full">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onBlur={() => handleCommit({})}
            className="w-full h-full border border-gray-300 rounded resize-none px-1"
            onKeyDown={handleTextAreaKeyDown}
          />
          <div className="flex justify-end mt-2 me-1">
            <FaCheck color="green" onClick={() => handleCommit({})} />
          </div>
        </div>
      ) : (
        <p className="text-black">{text}</p>
      )}
    </div>
  );
};

export default Sticky;
